import { createContext, useContext, useEffect, useState } from "react";
import { auth, googleProvider, db, messagingPromise } from "./firebase";
import { getToken } from "firebase/messaging";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  GoogleAuthProvider,
  getAuth,
} from "firebase/auth";
import { initializeApp, getApps, deleteApp } from "firebase/app";
import { doc, setDoc } from "firebase/firestore";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      setUser(u);
      setLoading(false);
      // Write email → uid index so other users can find this person by email
      if (u?.email) {
        // Internal email→uid index (for finding users within the app)
        setDoc(doc(db, "emails", u.email.toLowerCase()), { uid: u.uid }, { merge: true }).catch(()=>{});
        // Subscribe to Loops marketing list (upsert — safe to call on every sign-in)
        const firstName = u.displayName?.split(" ")[0] || "";
        fetch("/api/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: u.email, firstName }),
        }).catch(() => {});
      }
      // If notifications already granted, refresh FCM token silently
      if (u?.uid && typeof Notification !== "undefined" && Notification.permission === "granted") {
        const vapidKey = process.env.REACT_APP_FCM_VAPID_KEY;
        if (vapidKey) {
          messagingPromise.then(async messaging => {
            if (!messaging) return;
            const token = await getToken(messaging, { vapidKey }).catch(() => null);
            if (token) setDoc(doc(db, "users", u.uid), { fcmToken: token }, { merge: true }).catch(() => {});
          });
        }
      }
    });
    return unsub;
  }, []);

  const signUp       = (email, pw) => createUserWithEmailAndPassword(auth, email, pw);
  const signIn       = (email, pw) => signInWithEmailAndPassword(auth, email, pw);
  const signInGoogle = ()          => signInWithPopup(auth, googleProvider);
  const logOut       = ()          => signOut(auth);

  // Get a Google Calendar access token WITHOUT touching the main auth session.
  // We spin up a temporary secondary Firebase app, do the popup there, grab
  // the access token, then immediately tear down the secondary app.
  const connectCalendar = async () => {
    const { firebaseConfig } = await import("./firebase");
    const secondaryName = "calendarHelper_" + Date.now();
    const secondaryApp  = initializeApp(firebaseConfig, secondaryName);
    const secondaryAuth = getAuth(secondaryApp);
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope("https://www.googleapis.com/auth/calendar.readonly");
      provider.setCustomParameters({ prompt: "consent" });
      const result     = await signInWithPopup(secondaryAuth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      return credential?.accessToken || null;
    } finally {
      await deleteApp(secondaryApp);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signUp, signIn, signInGoogle, logOut, connectCalendar }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
